
import axiosClient from "./axiosClient";
import createFormData from "./formData";

const extractList = (response) => {
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const createNotFoundError = () => {
  const error = new Error("User not found.");

  error.response = {
    data: {
      message: "User not found.",
    },
  };

  return error;
};

const isBrowserFile = (value) => {
  return value instanceof File || value instanceof Blob;
};

const payloadHasFile = (payload = {}) => {
  return Object.values(payload).some((value) => isBrowserFile(value));
};

const createMultipartConfig = () => ({
  headers: {
    "Content-Type": "multipart/form-data",
  },
});

const createManagerRequest = (payload = {}) => {
  if (!payloadHasFile(payload)) {
    return {
      data: payload,
      config: {},
      multipart: false,
    };
  }

  return {
    data: createFormData(payload),
    config: createMultipartConfig(),
    multipart: true,
  };
};

export const userManagementApi = {
  // ==========================================
  // MANAGERS
  // ==========================================

  getManagers(params = {}) {
    return axiosClient.get("/managers", { params });
  },

  async getManager(userId) {
    const response = await axiosClient.get("/managers", {
      params: {
        per_page: 500,
      },
    });

    const user = extractList(response).find((record) => {
      return String(record.id) === String(userId);
    });

    if (!user) {
      throw createNotFoundError();
    }

    return {
      data: {
        data: user,
      },
    };
  },

  createManager(payload) {
    const request = createManagerRequest(payload);

    return axiosClient.post(
      "/managers",
      request.data,
      request.config,
    );
  },

  updateManager(userId, payload) {
    const request = createManagerRequest(payload);

    if (request.multipart) {
      const formData = createFormData({
        ...payload,
        _method: "PATCH",
      });

      return axiosClient.post(
        `/managers/${userId}`,
        formData,
        createMultipartConfig(),
      );
    }

    return axiosClient.patch(
      `/managers/${userId}`,
      request.data,
    );
  },

  updateManagerStatus(userId, isActive) {
    return axiosClient.patch(
      `/managers/${userId}/status`,
      {
        is_active: isActive,
      },
    );
  },

  // ==========================================
  // SALES REPRESENTATIVES
  // ==========================================

  getSalesRepresentatives(params = {}) {
    return axiosClient.get(
      "/sales-representatives",
      { params },
    );
  },

  getSalesRepresentative(userId) {
    return axiosClient.get(
      `/sales-representatives/${userId}`,
    );
  },

  createSalesRepresentative(payload) {
    return axiosClient.post(
      "/sales-representatives",
      payload,
    );
  },

  updateSalesRepresentative(userId, payload) {
    return axiosClient.patch(
      `/sales-representatives/${userId}`,
      payload,
    );
  },

  updateSalesRepresentativeStatus(userId, isActive) {
    return axiosClient.patch(
      `/sales-representatives/${userId}/status`,
      {
        is_active: isActive,
      },
    );
  },

  // ==========================================
  // RESTAURANT STAFF
  // ==========================================

  getStaff(establishmentId, params = {}) {
    return axiosClient.get(
      `/establishments/${establishmentId}/staff`,
      { params },
    );
  },

  async getStaffUser(establishmentId, userId) {
    const response = await axiosClient.get(
      `/establishments/${establishmentId}/staff`,
    );

    const user = extractList(response).find((record) => {
      return String(record.id) === String(userId);
    });

    if (!user) {
      throw createNotFoundError();
    }

    return {
      data: {
        data: user,
      },
    };
  },

  createStaff(establishmentId, payload) {
    return axiosClient.post(
      `/establishments/${establishmentId}/staff`,
      payload,
    );
  },

  updateStaff(establishmentId, userId, payload) {
    return axiosClient.patch(
      `/establishments/${establishmentId}/staff/${userId}`,
      payload,
    );
  },

  updateStaffStatus(establishmentId, userId, isActive) {
    return axiosClient.patch(
      `/establishments/${establishmentId}/staff/${userId}/status`,
      {
        is_active: isActive,
      },
    );
  },

  deleteStaff(establishmentId, userId) {
    return axiosClient.delete(
      `/establishments/${establishmentId}/staff/${userId}`,
    );
  },
};

export default userManagementApi;
